<script lang="ts">
  import {
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
  } from '@skeletonlabs/floating-ui-svelte';
  import type { Snippet } from 'svelte';
  import Portal from 'svelte-portal';
  import { type BtnType, buttonClassFn, type Size } from '../controls/button.styles';
  import { createCloseWatcher } from '$lib/utils/closeWatcher';
  import { onDestroy } from 'svelte';
  import { isCloseWatcherAvailable } from '$lib/utils/support';
  import type { TransformOrigin } from '$lib/utils/transitions';

  type Placement = 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

  interface Props {
    btnType?: BtnType;
    size?: Size;
    iconOnly?: boolean;
    round?: boolean;
    title?: string | undefined;
    disabled?: boolean | undefined;
    placement?: Placement;
    appendTo?: string | HTMLElement;
    openOnFocus?: boolean;
    openOnHover?: boolean;
    openOnKeyboardClick?: boolean;
    delay?: number | { open?: number; close?: number };
    containerClass?: string | Array<string>;
    anchorRegionClass?: string | Array<string>;
    onVisibleChange?: (visible: boolean) => void;
    anchorContent?: Snippet<[Record<string, unknown>]>;
    buttonContent?: Snippet;
    popoverContent?: Snippet<
      [
        {
          onClose: () => void;
          transformOrigin: TransformOrigin;
        },
      ]
    >;
  }

  let {
    btnType = 'primary',
    size = 'normal',
    iconOnly = false,
    round = false,
    title,
    disabled,
    placement = 'bottom',
    appendTo,
    openOnFocus = false,
    openOnHover = false,
    openOnKeyboardClick = true,
    delay,
    containerClass,
    anchorRegionClass,
    onVisibleChange,
    anchorContent,
    buttonContent,
    popoverContent,
  }: Props = $props();

  let open = $state(false);

  const closeWatcher = createCloseWatcher({
    onClose: () => updateOpenState(false),
  });

  const floating = $derived(
    useFloating({
      whileElementsMounted: autoUpdate,
      get open() {
        return open;
      },
      onOpenChange: (v) => {
        if (v) {
          handleOpen();
        } else {
          handleClose();
        }
      },
      placement,
      get middleware() {
        return [offset(10), flip(), shift()];
      },
    }),
  );
  const role = $derived(useRole(floating.context));
  const click = $derived(useClick(floating.context, { keyboardHandlers: openOnKeyboardClick }));
  const dismiss = $derived(useDismiss(floating.context, { escapeKey: !isCloseWatcherAvailable() }));
  const interactions = $derived(
    useInteractions(
      [
        role,
        dismiss,
        click,
        openOnFocus ? useFocus(floating.context) : null,
        openOnHover ? useHover(floating.context, { delay }) : null,
      ].filter((props) => !!props),
    ),
  );

  const popoverContentClass = 'z-40';
  const popoverButtonClass = $derived(buttonClassFn({ btnType, size, iconOnly, round }));
  const transformOrigin = $derived.by(() => {
    const currentPlacement = floating.placement;
    const placementToOrigin: Record<string, TransformOrigin> = {
      top: 'bottom center',
      'top-start': 'bottom left',
      'top-end': 'bottom right',
      bottom: 'top center',
      'bottom-start': 'top left',
      'bottom-end': 'top right',
      left: 'right center',
      right: 'left center',
    };

    return placementToOrigin[currentPlacement] || 'top center';
  });

  onDestroy(() => {
    closeWatcher.cleanup();
  });

  export function setOpen(newOpen: boolean): void {
    open = newOpen;
  }

  function handleOpen(): void {
    closeWatcher.setup();
    updateOpenState(true);
  }

  function handleClose(): void {
    if (closeWatcher.isActive) {
      closeWatcher.requestClose();
    } else {
      updateOpenState(false);
    }
  }

  function updateOpenState(newOpen: boolean): void {
    open = newOpen;
    onVisibleChange?.(newOpen);
  }
</script>

{#snippet popoverContentWrapper()}
  <div
    class={popoverContentClass}
    data-testid="popover"
    style={floating.floatingStyles}
    {...interactions.getFloatingProps()}
    bind:this={floating.elements.floating}
  >
    {@render popoverContent?.({ onClose: () => (open = false), transformOrigin })}
  </div>
{/snippet}

<div class={containerClass}>
  {#if anchorContent}
    <div class={anchorRegionClass} bind:this={floating.elements.reference}>
      {@render anchorContent?.(interactions.getReferenceProps())}
    </div>
  {:else}
    <button
      class={popoverButtonClass}
      {disabled}
      {title}
      {...interactions.getReferenceProps()}
      bind:this={floating.elements.reference}
    >
      {@render buttonContent?.()}
    </button>
  {/if}
  {#if open}
    {#if appendTo}
      <Portal target={appendTo}>
        {@render popoverContentWrapper()}
      </Portal>
    {:else}
      {@render popoverContentWrapper()}
    {/if}
  {/if}
</div>
